/**
 * Trace debug panel — visible only when the URL has `?debug=1`.
 *
 * Fetches the executed-tool trace from `GET /api/operations-agent/trace/:runId`
 * (JWT-gated) and renders it as a compact list. This
 * surface is for engineers debugging the planned harness; it is intentionally
 * minimal and unstyled beyond Tailwind defaults.
 */

import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';
import { getAuthHeaders } from '../../utils/apiClient';

interface TraceEntry {
  iteration: number;
  name: string;
  args: Record<string, unknown>;
  result?: { claim_count: number; limitation_count: number; cached: boolean };
  error?: string;
  latency_ms: number;
}

interface TraceSnapshot {
  run_id: string;
  question: string;
  plan?: {
    question_type: string;
    proposed_tools: Array<{ name: string; rationale: string }>;
    success_criteria: string[];
  };
  /** Bound-plan runs (`harness_mode: 'bound_plan'`) carry this instead of `plan`. */
  execution_plan?: {
    question_understanding: string;
    steps: Array<{ id: string; tool: string; args: Record<string, unknown> }>;
  };
  validated_window?: { from: string; to: string };
  trace: TraceEntry[];
  reflections: Array<{ state: string; gaps: string[]; confidence: string }>;
  verification?: {
    ok: boolean;
    issues: string[];
    metrics: {
      claim_count: number;
      evidence_ref_count: number;
      unknown_count: number;
      distinct_capabilities_called: number;
    };
  };
  harness_mode: string;
  computed_at: string;
}

export default function TraceDebugPanel({ runId }: { runId: string }) {
  const [snapshot, setSnapshot] = useState<TraceSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const headers = await getAuthHeaders();
        const base = API_ENDPOINTS.base || '';
        const res = await fetch(`${base}/api/operations-agent/trace/${encodeURIComponent(runId)}`, {
          headers,
        });
        if (!res.ok) throw new Error(`trace endpoint returned ${res.status}`);
        const json = (await res.json()) as TraceSnapshot;
        if (!cancelled) setSnapshot(json);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [runId]);

  return (
    <div className="bg-warning/10 rounded-xl border border-warning/40 p-3 text-xs text-warning/90">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 font-mono uppercase tracking-wider text-warning/80"
      >
        <span>{open ? '▼' : '▶'} Trace debug · {runId}</span>
        {loading && <Loader2 className="w-3 h-3 animate-spin" />}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          {error && <div className="text-destructive">Failed to load trace: {error}</div>}
          {!error && !loading && snapshot && (
            <>
              <section>
                <div className="font-semibold text-warning">Plan</div>
                {snapshot.plan ? (
                  <ul className="mt-1 space-y-0.5">
                    <li>question_type: {snapshot.plan.question_type}</li>
                    <li>
                      proposed_tools:{' '}
                      {snapshot.plan.proposed_tools.map((t) => t.name).join(', ')}
                    </li>
                    <li>
                      success_criteria:{' '}
                      {snapshot.plan.success_criteria.join(' · ') || '(none)'}
                    </li>
                  </ul>
                ) : snapshot.execution_plan ? (
                  <ul className="mt-1 space-y-0.5">
                    <li>bound plan: {snapshot.execution_plan.question_understanding}</li>
                    {snapshot.execution_plan.steps.map((step) => (
                      <li key={step.id} className="font-mono">
                        {step.id}: {step.tool} {JSON.stringify(step.args)}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-warning/60">no plan recorded ({snapshot.harness_mode} harness)</div>
                )}
              </section>

              {snapshot.validated_window && (
                <section>
                  <div className="font-semibold text-warning">Validated window</div>
                  <div>
                    {snapshot.validated_window.from} → {snapshot.validated_window.to}
                  </div>
                </section>
              )}

              <section>
                <div className="font-semibold text-warning">
                  Trace ({snapshot.trace.length} call{snapshot.trace.length !== 1 ? 's' : ''})
                </div>
                <ol className="mt-1 space-y-1">
                  {snapshot.trace.map((t, i) => (
                    <li key={i} className="font-mono">
                      <span className="text-warning">[{t.iteration}]</span> {t.name}
                      {' · '}
                      {t.error
                        ? <span className="text-destructive">error: {t.error}</span>
                        : <>{t.result?.claim_count ?? 0}c / {t.result?.limitation_count ?? 0}l{t.result?.cached ? ' · cached' : ''}</>
                      }
                      {' · '}
                      {t.latency_ms}ms
                    </li>
                  ))}
                </ol>
              </section>

              {snapshot.reflections.length > 0 && (
                <section>
                  <div className="font-semibold text-warning">
                    Reflections ({snapshot.reflections.length})
                  </div>
                  <ol className="mt-1 space-y-0.5">
                    {snapshot.reflections.map((r, i) => (
                      <li key={i}>
                        round {i + 1}: {r.state} · confidence {r.confidence}
                        {r.gaps.length > 0 && ` · gaps: ${r.gaps.join('; ')}`}
                      </li>
                    ))}
                  </ol>
                </section>
              )}

              {snapshot.verification && (
                <section>
                  <div className="font-semibold text-warning">Verification</div>
                  <div>
                    {snapshot.verification.ok ? '✓ ok' : '✗ issues'} ·{' '}
                    {snapshot.verification.metrics.claim_count} claim
                    {snapshot.verification.metrics.claim_count !== 1 ? 's' : ''} ·{' '}
                    {snapshot.verification.metrics.distinct_capabilities_called} tool
                    {snapshot.verification.metrics.distinct_capabilities_called !== 1 ? 's' : ''}
                  </div>
                  {snapshot.verification.issues.length > 0 && (
                    <ul className="mt-1 list-disc list-inside">
                      {snapshot.verification.issues.map((iss, i) => (
                        <li key={i} className="text-destructive">{iss}</li>
                      ))}
                    </ul>
                  )}
                </section>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Returns true when the page has `?debug=1` (or `?debug=true`). */
export function isDebugMode(): boolean {
  if (typeof window === 'undefined') return false;
  const v = new URLSearchParams(window.location.search).get('debug');
  return v === '1' || v === 'true';
}
